import React from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import './Slider.css';
import { useNavigate } from 'react-router-dom';

const heroHighlights = [
  'Smart algorithms that prevent over-prep',
  'Automated reports that prove your impact',
  'Kind to the planet',
];

const heroStats = [
  { label: 'Saved for Clients', prefix: 'Kr', endValue: 12, suffix: 'M+' },
  { label: 'Fresh Meals Served', endValue: 120, suffix: 'K+' },
  { label: 'Happy Eaters', endValue: 3000, suffix: '+' },
];

export const Slider = () => {
  const navigate = useNavigate();
  const [statValues, setStatValues] = React.useState(heroStats.map(() => 0));
  const cardRef = React.useRef(null);
  const animationFrameRef = React.useRef();

  const runAnimation = React.useCallback(() => {
    cancelAnimationFrame(animationFrameRef.current);
    const duration = 1500;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setStatValues(heroStats.map((stat) => Math.floor(stat.endValue * progress)));
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(tick);
      }
    };

    animationFrameRef.current = requestAnimationFrame(tick);
  }, []);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          runAnimation();
        } else {
          cancelAnimationFrame(animationFrameRef.current);
          setStatValues(heroStats.map(() => 0));
        }
      },
      { threshold: 0.4 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      observer.disconnect();
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [runAnimation]);

  const handleTalkToTeam = () => {
    window.open('tel:+4741169460');
  };

  return (
    <Box
      className='slider'
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(130deg, #F9F3EC 0%, #F5F7FB 50%, #FFFFFF 100%)',
        py: { xs: 10, md: 14 },
      }}
    >
      <Box
        component='video'
        src='/assets/HomeVideo.mp4'
        autoPlay
        muted
        loop
        playsInline
        aria-hidden='true'
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
          pointerEvents: 'none',
          '@media (prefers-reduced-motion: reduce)': {
            display: 'none',
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(130deg, rgba(249, 243, 236, 0.72) 0%, rgba(245, 247, 251, 0.6) 50%, rgba(255, 255, 255, 0.7) 100%)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: '-20% auto auto -10%',
          width: '45%',
          height: '45%',
          background: 'radial-gradient(closest-side, rgba(47,58,114,0.08), transparent)',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />
      <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 3 }}>
        <Grid container spacing={{ xs: 6, md: 10 }} alignItems='center'>
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Chip
                label='For workplaces that love food'
                color='secondary'
                variant='outlined'
                sx={{ alignSelf: 'flex-start', fontSize: '1.3rem', fontWeight: 500, borderRadius: 999 }}
              />
              <Typography variant='h1' sx={{ maxWidth: 520 }}>
                FoodFe - The smartest kitchen system in Norway.
              </Typography>
              <Typography variant='body1' sx={{ maxWidth: 560 }}>
                Stop guessing. Cook exactly what’s ordered. FoodFe connects your kitchen to real-time demand, so you prep exactly what’s needed. Enjoy fresh, personalised meals daily while you automate the savings.
              </Typography>
              <Button
                variant='outlined'
                color='primary'
                size='large'
                startIcon={<PhoneRoundedIcon />}
                onClick={handleTalkToTeam}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
              >
                Book a Demo
              </Button>
              <Stack spacing={1.5}>
                {heroHighlights.map((item) => (
                  <Stack key={item} direction='row' alignItems='center' spacing={1.5}>
                    <CheckCircleRoundedIcon sx={{ color: 'primary.main', fontSize: '2rem' }} />
                    <Typography variant='subtitle1'>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: { xs: '80%', sm: '66%', md: '70%' },
                  maxWidth: 440,
                  aspectRatio: '1 / 1',
                  borderRadius: '50%',
                  mx: 'auto',
                  overflow: 'hidden',
                  boxShadow: '0px 32px 60px rgba(31, 36, 51, 0.18)',
                }}
              >
                <Box
                  component='img'
                  src='/assets/images/DK1.jpg'
                  alt='Workplace lunch being served'
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    objectFit: 'cover',
                  }}
                />
              </Box>
              <Paper
                elevation={0}
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: { xs: '90%', sm: '88%', md: '92%' },
                  transform: { xs: 'translate(-50%, -50%)', md: 'translate(-50%, -50%)' },
                  px: { xs: 3.5, md: 5 },
                  py: { xs: 3, md: 4 },
                  minWidth: { xs: '95%', sm: '91%', md: 600 },
                  maxWidth: 740,
                  borderRadius: 28,
                  backdropFilter: 'blur(8px)',
                  background: 'rgba(255, 255, 255, 0.92)',
                  boxShadow: '0px 24px 50px rgba(31, 36, 51, 0.16)',
                }}
                ref={cardRef}
              >
                <Stack spacing={3} alignItems='center'>
                  <Typography variant='bodySmallCaps' sx={{ textAlign: 'center', letterSpacing: '0.16em' }}>
                    Impact snapshot
                  </Typography>
                  <Stack
                    direction='row'
                    spacing={3}
                    divider={
                      <Divider
                        orientation='vertical'
                        flexItem
                        sx={{ borderColor: 'divider', opacity: 0.5 }}
                      />
                    }
                    alignItems='stretch'
                    justifyContent='center'
                  >
                    {heroStats.map((stat, index) => (
                      <Stack
                        key={stat.label}
                        spacing={0.5}
                        alignItems='center'
                        sx={{ textAlign: 'center' }}
                      >
                        <Typography variant='h4' color='primary'>
                          {`${stat.prefix ?? ''} ${statValues[index].toLocaleString()}${stat.suffix ?? ''}`}
                        </Typography>
                        <Typography variant='subtitle2'>{stat.label}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
